/* eslint-disable react-hooks/exhaustive-deps */
import {Snackbar, Typography} from '@mui/material'
import React, {useEffect, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import TableCustom, {PaginationType, TableColumn} from 'src/components/elements/TableCustom/TableCustom'
import {Action} from 'src/components/elements/Menus/ActionMenu/ActionsMenu'

import LayoutContainer from 'src/components/layouts/LayoutContainer/LayoutContainer'
import Filters, {FilterItem} from 'src/components/templates/Filters/Filters'
import {PATHS, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import EchocardiographicRequestDialog from 'src/components/templates/EchocardiographicRequestDialog/EchocardiographicRequestDialog'
import {mdiDownload, mdiEye} from '@mdi/js'
import FileViewerModal from 'src/components/elements/FileViewerModal/FileViewerModal'
import {useAppSelector} from 'src/hooks'

const EchocardiographicRequests = () => {
  const echoColumnOptions = useAppSelector(
    state => state.pspConfigReducer.data.echocardiographicRequestsConfigurations?.columnOptions
  )

  const DEFAULT_COLUMNS : TableColumn[] = [
    {
      id: 'createdAt',
      label: 'DATA RICHIESTA',
      type: 'date',
      format: 'ddd DD MMM YYYY'
    },
    {
      id: 'createdAt',
      label: 'ORARIO',
      type: 'date',
      format: 'HH:mm'
    },
    {
      id: 'title',
      label: 'TITOLO'
    },
    {
      id: 'examType',
      label: 'TIPO ESAME',
      render: (value: string) => {
        switch (value) {
          case 'eco':
            return 'ECO'
          default:
            return value
        }
      }
    },
    {
      id: 'patient',
      label: 'PAZIENTE DI RIFERIMENTO',
      noSort: true
    },
    {
      id: 'state',
      label: 'STATO REFERTAZIONE',
      noSort: true,
      render: (value: string) => {
        switch (value) {
          case 'TO_BE_COMPLETED':
            return 'Da completare'
          case 'COMPLETED':
            return 'Completato'
          case 'CANCELLED':
            return 'Annullato'
          default:
            return value
        }
      }
    }
  ]

  const columns : TableColumn[] = echoColumnOptions?.length > 0 ? echoColumnOptions : DEFAULT_COLUMNS

  const statusOptions = [
    {
      value: 'TO_BE_COMPLETED',
      label: 'Da completare'
    },
    {
      value: 'COMPLETED',
      label: 'Completato'
    }
  ]

  const filterConfig: FilterItem[] = [
    {
      id: 'patient',
      label: 'Paziente',
      type: 'patientLookup'
    },
    {
      id: 'examType',
      label: 'Tipo esame',
      type: 'select',
      options: [{value: 'eco', label: 'ECO'}]
    },
    {
      id: 'rangeDates',
      label: 'Data',
      type: 'rangePicker'
    },
    {
      id: 'state',
      label: 'Stato',
      type: 'select',
      options: statusOptions
    }
  ]

  const [filters, setFilters] = useState<Record<string, string | string[]>[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<any>(undefined)
  const [statusCount, setStatusCount] = useState<any>(undefined)
  const [isRequestModal, setIsRequestModal] = useState<boolean>(false)
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: '-createdAt'
  })
  const [statusDelete, setStatusDelete] = useState<any>(undefined)
  const [statusPost, setStatusPost] = useState<any>(undefined)
  const [postMessage, setPostMessage] = useState<any>(undefined)
  const [openViewModal, setOpenViewModal] = useState<boolean>(false)
  const [MIMETypeValue, setMIMETypeValue] = useState<any>()
  const [file, setFile] = useState<any>()
  const [fileName, setFileName] = useState<any>()
  const [statusDownload, setStatusDownload] = useState<any>(undefined)
  const [selectedRequest, setSelectedRequest] = useState<any>(undefined)
  const [isReadOnlyModal, setIsReadOnlyModal] = useState<boolean>(false)

  const handleModalView = async (row: any) => {
    setOpenViewModal(true)
    const filePath = await fetchFile(row.patientId, row.examId, 'application/pdf')
    setMIMETypeValue('application/pdf')
    setFile(filePath)
    setFileName('referto.pdf')
  }

  const fetchFile = async (patientId: string, examId: string, mime?: string) => {
    const fileResponse = await apiClient.downloadMedicalReport(patientId, examId, setStatusDownload)
    const blob = new Blob([new Uint8Array(fileResponse)], {type: mime})
    return (window.URL || window.webkitURL).createObjectURL(blob)
  }

  const rowActions: Action[] = [
    {
      id: 'download',
      label: 'Scarica referto',
      icon: mdiDownload,
      action: handleModalView,
      condition: (row: any) => row.examId
    },
    {
      id: 'details',
      label: 'Dettaglio',
      icon: mdiEye,
      action: (row: any) => {
        setSelectedRequest(row)
        setIsReadOnlyModal(true)
      }
    }
  ]

  const fetchData = async () => {
    const fetchedData = await apiClient.getExamsRequests(filters, setStatus, pagination) ?? []
    const parsedData = fetchedData.map((examRequest: any) => {
      return {
        ...examRequest,
        patient: `${examRequest.patient.firstName} ${examRequest.patient.lastName}`
      }
    })
    setData(parsedData)
    const fetchedDataCount = await apiClient.getExamsRequestsCount(filters, setStatusCount) ?? []
    setCount(fetchedDataCount)
  }

  const saveEchocardiographicRequest = async (values: any) => {
    setPostMessage("Errore nel richiedere l'esame")
    await apiClient.postExamRequest(values, setStatusPost)
    await fetchData()
    setIsRequestModal(false)
  }

  useEffect(() => {
    fetchData()
  }, [filters, pagination])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) {
      setOpenViewModal(false)
    }
  }, [statusDownload])

  const onSubmit = (values: any) => {
    setFilters(values)
  }

  return (
    <LayoutContainer
      breadcrumbs={[{label: 'Lista richieste esami', href: PATHS.ECHOCARDIOGRAPHIC_REQUESTS}]}
      buttons={[
        <PrimaryButton key='request' onClick={() => setIsRequestModal(true)}>
          <Typography style={{fontSize: 12, fontWeight: 600}}>
            <FormattedMessage id='request' />
          </Typography>
        </PrimaryButton>,
        <Filters config={filterConfig} key='filters' onSubmit={onSubmit} />
      ]}
    >
      <div style={{marginTop: '16px'}}>
        <TableCustom
          actions={rowActions}
          columns={columns}
          onPaginationChange={setPagination}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </div>
      {isRequestModal &&
        <EchocardiographicRequestDialog
          onClose={() => setIsRequestModal(false)}
          onSave={saveEchocardiographicRequest}
        />}
      {isReadOnlyModal &&
        <EchocardiographicRequestDialog
          initialValues={selectedRequest}
          onClose={() => setIsReadOnlyModal(false)}
          onSave={() => {}}
          readOnly
        />}
      <FileViewerModal
        MIMETypeValue={MIMETypeValue}
        fileName={fileName}
        filepath={file}
        handleClose={() => { setOpenViewModal(false); setFile(undefined); setMIMETypeValue(undefined); setFileName(undefined) }}
        open={openViewModal}
        status={statusDownload}
      />
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
        open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatus(undefined); setStatusCount(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {'Errore nel caricare i dati della pagina.'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatusDelete(undefined) }}
        open={statusDelete === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatusDelete(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {'Errore nel cancellare.'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatusPost(undefined) }}
        open={statusPost === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatusPost(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {postMessage}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </LayoutContainer>
  )
}

export default EchocardiographicRequests
